// ==================== PROTECTED ROUTE COMPONENT ====================

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts';
import { ROUTES } from '../../constants';
import { Loader2 } from 'lucide-react';
import { hasValidSubscriptionAccess } from '../../utils/subscription';

// ==================== TYPES ====================

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: ('SUPER_ADMIN' | 'CUSTOMER')[];
  requireActiveAccount?: boolean;
  requireSubscription?: boolean;
}

// ==================== LOADING COMPONENT ====================

const LoadingScreen = () => (
  <div className="min-h-screen bg-(--color-dark-navy) flex items-center justify-center">
    <div className="text-center">
      <Loader2 size={48} className="text-(--color-signal-orange) animate-spin mx-auto mb-4" />
      <p className="text-(--color-soft-gray)">Loading...</p>
    </div>
  </div>
);

// ==================== PROTECTED ROUTE ====================

export const ProtectedRoute = ({
  children,
  allowedRoles,
  requireActiveAccount = true,
  requireSubscription = false,
}: ProtectedRouteProps) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading while checking auth
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to={ROUTES.AUTH.LOGIN} state={{ from: location }} replace />;
  }

  // Check role restriction
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect based on user's actual role
    if (user.role === 'SUPER_ADMIN') {
      return <Navigate to={ROUTES.ADMIN.DASHBOARD} replace />;
    }
    return <Navigate to={ROUTES.CUSTOMER.DASHBOARD} replace />;
  }

  // Customer-specific checks
  if (user.role === 'CUSTOMER') {
    // Check if account is active (approved)
    if (requireActiveAccount && !user.isActive) {
      return <Navigate to={ROUTES.CUSTOMER.ROOT + '/pending'} replace />;
    }

    // Check subscription requirement
    // CANCELLED subscriptions with remaining time should still have access
    if (requireSubscription) {
      if (!hasValidSubscriptionAccess(user.organization?.subscription)) {
        return <Navigate to={ROUTES.CUSTOMER.ROOT + '/select-plan'} replace />;
      }
    }
  }

  return <>{children}</>;
};

// ==================== PUBLIC ONLY ROUTE ====================
// Redirect authenticated users away from login/register pages

export const PublicOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated && user) {
    // Redirect to appropriate dashboard
    if (user.role === 'SUPER_ADMIN') {
      return <Navigate to={ROUTES.ADMIN.DASHBOARD} replace />;
    }
    
    // Customer redirects
    if (!user.isActive) {
      return <Navigate to={ROUTES.CUSTOMER.ROOT + '/pending'} replace />;
    }
    
    // Check subscription access (CANCELLED with remaining time should go to dashboard)
    if (!hasValidSubscriptionAccess(user.organization?.subscription)) {
      return <Navigate to={ROUTES.CUSTOMER.ROOT + '/select-plan'} replace />;
    }
    
    return <Navigate to={ROUTES.CUSTOMER.DASHBOARD} replace />;
  }

  return <>{children}</>;
};

// ==================== EXPORTS ====================

export default ProtectedRoute;

